import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Lock, Compass, Users } from 'lucide-react';
import PlanGate from '../../../../../../components/gating/PlanGate';
import { getTimeAgo } from '../../utils/dashboardHelpers';

const LiveLocationCard = ({ voucher, guests = [], handlePingUser }) => {
  const navigate = useNavigate();
  const [showAllGuests, setShowAllGuests] = useState(false);
  const isBookingsRole = localStorage.getItem('sub_user_role') === 'bookings';

  const guestList = (Array.isArray(guests) && guests.length > 0)
    ? guests
    : (Array.isArray(voucher?.active_guests) ? voucher.active_guests : []);

  // Prevent background scrolling and enable Escape close when "View All Guests" modal is active
  useEffect(() => {
    if (!showAllGuests) return;
    const prevBodyOverflow = document.body.style.overflow;
    const mainEl = document.querySelector('main');
    const prevMainOverflow = mainEl ? mainEl.style.overflow : '';

    document.body.style.overflow = 'hidden';
    if (mainEl) mainEl.style.overflow = 'hidden';

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setShowAllGuests(false);
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = prevBodyOverflow;
      if (mainEl) mainEl.style.overflow = prevMainOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showAllGuests]);

  return (
    <>
      <PlanGate
        requiredPlan="pro"
        featureName="Live Location Tracking"
        tooltipText="Live location tracking requires the Pro Plan."
        showLockIcon={false}
        wrapperClassName="w-full h-full flex flex-col"
      >
        {({ isLocked, onClick }) => (
          <div className="vc-card relative h-full flex-1 flex flex-col">
            <div className="vc-card-head flex items-center justify-between pb-2 border-b border-slate-100">
              <h3 className="flex items-center gap-2 text-sm font-medium text-slate-600 tracking-tight">
                <span className="w-7 h-7 rounded-lg bg-teal-50 border border-teal-100 flex items-center justify-center shrink-0">
                  <Compass size={15} className="text-[#067978]" />
                </span>
                <span>Live Location</span>
              </h3>
              {isLocked && (
                <div onClick={onClick} className="cursor-pointer" title="Pro Plan Required">
                  <Lock size={13} className="text-slate-400" />
                </div>
              )}
            </div>

            <div className="vc-today-list mt-2 space-y-3">
              {guestList.length > 0 ? (
                guestList.slice(0, 3).map((g, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between py-2 border-b border-[#e7ecea] last:border-0 last:pb-0 mb-1 gap-2"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1 pr-1">
                      <span
                        className={`w-2 h-2 rounded-full shrink-0 ${
                          g.is_tracking ? 'bg-[#067978] animate-pulse' : 'bg-gray-300'
                        }`}
                      />
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className="font-medium text-slate-700 tracking-wide truncate text-[11.5px]">
                          {g.name}
                        </span>
                        <span className="text-[9px] text-[#8a9aa5] font-medium truncate mt-0.5">
                          {g.is_tracking ? (
                            <>Active • <b>{g.last_location?.city || "Unknown City"}</b></>
                          ) : (
                            `Seen: ${getTimeAgo(g.last_seen)}`
                          )}
                        </span>
                      </div>
                    </div>

                    <div className="flex gap-1.5 shrink-0 items-center">
                      {!isBookingsRole && (
                        <button
                          onClick={
                            isLocked
                              ? onClick
                              : () => navigate(`/tracking/map?voucherId=${voucher.booking_id}`)
                          }
                          className="px-2.5 py-0.5 text-[10px] font-medium text-[#067978] border border-[#067978] bg-white hover:bg-teal-50/70 rounded-full transition-all text-center cursor-pointer flex items-center gap-1 active:scale-95 shadow-2xs"
                        >
                          <span>Track</span>
                          {isLocked && <Lock size={9} className="text-[#067978]/80" />}
                        </button>
                      )}
                      <button
                        onClick={isLocked ? onClick : () => handlePingUser(g.name)}
                        className="px-2.5 py-0.5 text-[10px] font-medium text-[#067978] border border-[#067978] bg-white hover:bg-teal-50/70 rounded-full transition-all text-center flex items-center gap-1 cursor-pointer active:scale-95 shadow-2xs"
                      >
                        <span>Ping</span>
                        {isLocked && <Lock size={9} className="text-[#067978]/80" />}
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div
                  className={`text-sm text-[#8a9aa5] ${isLocked ? 'cursor-pointer' : ''}`}
                  onClick={isLocked ? onClick : undefined}
                >
                  No guests currently connected.
                </div>
              )}
            </div>

            {/* View guests footer cue: "View 2 guests" (no "all") */}
            {guestList.length > 0 && (
              <div
                className="vc-expand-cue mt-auto pt-3 cursor-pointer flex items-center justify-between"
                onClick={isLocked ? onClick : () => setShowAllGuests(true)}
              >
                <span>{`View ${guestList.length} ${guestList.length === 1 ? 'guest' : 'guests'} ⌄`}</span>
                {isLocked && <Lock size={11} className="text-slate-400" />}
              </div>
            )}
          </div>
        )}
      </PlanGate>

      {/* View All Guests Modal */}
      {typeof document !== 'undefined' && guestList.length > 0 && createPortal(
        <div
          className={`vc-overlay ${showAllGuests ? 'vc-open' : ''}`}
          style={{ top: 'var(--portal-header-height, 64px)', height: 'calc(100vh - var(--portal-header-height, 64px))' }}
          onClick={(e) => {
            if (e.target.classList.contains('vc-overlay')) setShowAllGuests(false);
          }}
        >
          <div className="vc-modal !max-w-xl" role="dialog" aria-modal="true" aria-label="Guest List">
            <div className="vc-m-head">
              <h4 className="flex items-center gap-2 text-base font-semibold text-slate-800">
                <Users size={16} className="text-[#067978]" />
                <span>Guest List</span>
              </h4>
              <button className="vc-m-close" onClick={() => setShowAllGuests(false)}>✕</button>
            </div>
            <div className="vc-m-body space-y-4">
              {guestList.length > 0 ? (
                guestList.map((g, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between text-sm py-2 border-b border-[#e7ecea] last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`w-2.5 h-2.5 rounded-full shrink-0 ${
                          g.is_tracking ? 'bg-[#067978] animate-pulse' : 'bg-gray-300'
                        }`}
                      />
                      <div>
                        <div className="font-medium text-slate-700 text-xs sm:text-sm">{g.name}</div>
                        <div className="text-[11px] text-slate-400 font-normal">
                          {g.is_tracking ? (
                            <span className="text-[#067978] font-normal">
                              Active in {g.last_location?.city || "Unknown City"}
                            </span>
                          ) : (
                            `Last seen ${getTimeAgo(g.last_seen)}`
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {!isBookingsRole && (
                        <button
                          onClick={() => {
                            setShowAllGuests(false);
                            navigate(`/tracking/map?voucherId=${voucher.booking_id}`);
                          }}
                          className="px-3 py-1 text-xs font-normal text-[#067978] border border-[#067978] bg-white hover:bg-teal-50 rounded-full transition-all cursor-pointer shadow-2xs"
                        >
                          Track
                        </button>
                      )}
                      <button
                        onClick={() => handlePingUser(g.name)}
                        className="px-3 py-1 text-xs font-normal text-[#067978] border border-[#067978] bg-white hover:bg-teal-50 rounded-full transition-all cursor-pointer shadow-2xs"
                      >
                        Ping
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-slate-400 text-center py-4">No guests found.</div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default LiveLocationCard;
